import type { SerializedEditorState } from "lexical";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconCheckCircle } from "../../../../common/icons/IconCheckCircle/IconCheckCircle";
import "./Notes.scss";
import { NotesEditor } from "./NotesEditor/NotesEditor";
import { NotesViewer } from "./NotesViewer/NotesViewer";
import { notesOnError, parseNoteContent } from "./notesConfig";
import LeafButton, { Variant } from "../../../../common/leaf/LeafButton/LeafButton";
import { LeafModal } from "../../../../common/leaf/LeafModal/LeafModal";
import LeafToastMessage from "../../../../common/leaf/LeafToastMessage/LeafToastMessage";
import {
  useCreateProfileNoteMutation,
  useDeleteProfileNoteMutation,
  useGetProfileNotesQuery,
  useUpdateProfileNoteMutation,
} from "../../../../services/AboProfile/AboProfileAPI";
import { useAppSelector } from "../../../../store";
import defaultContent from "../../../../content/downlineProfile";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import {
  trackAddNewNote,
  trackCancelNoteChanges,
  trackCloseNotesModal,
  trackDeleteNote,
  trackDeleteNoteModalOpen,
  trackEditNote,
  trackSaveNote,
} from "../../PlatinumProfile/downlineProfileTealium";
import { IconNoteCreate } from "../../../../common/icons/IconNoteCreate/IconNoteCreate";

const TOAST_DURATION_MS = 4000;
const Notes = () => {
  const { t } = useTranslation(["downlineProfile"]);
  const affAbo = useAppSelector((state) => state.downlineProfile.affAbo);
  const disableImpersonationNoteSave = useAppSelector((state) => state.boot.configuration.disableImpersonationNoteSave);
  const isImpersonation = useAppSelector((state) => state.boot.isImpersonation);
  const noteSaveDisabled = isImpersonation === true && disableImpersonationNoteSave === true;
  const [editMode, setEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [savedContent, setSavedContent] = useState<SerializedEditorState | null>(null);
  const [savedTimestamp, setSavedTimestamp] = useState<number | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const { isLoading: isNotesLoading, data: notesData } = useGetProfileNotesQuery({ affabo: affAbo }, { skip: !affAbo });
  const [createNote, { isLoading: isCreating }] = useCreateProfileNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateProfileNoteMutation();
  const [deleteNote, { isLoading: isDeleting }] = useDeleteProfileNoteMutation();

  const note = notesData?.notes?.[0] ?? null;
  const noteContent = isDeleted ? null : (savedContent ?? parseNoteContent(note?.content));
  const hasNotes = noteContent !== null;
  const lastEdited = savedTimestamp ?? note?.lastModified ?? null;
  const isSaving = isCreating || isUpdating;

  const handleSave = async (state: SerializedEditorState) => {
    if (noteSaveDisabled) return;
    const content = JSON.stringify(state);
    const isUpdate = note !== null && !isDeleted;
    try {
      const { lastModified } = isUpdate
        ? await updateNote({ noteId: note.noteId, content }).unwrap()
        : await createNote({ affabo: affAbo, content }).unwrap();
      setSavedContent(state);
      setSavedTimestamp(lastModified);
      setIsDeleted(false);
      setEditMode(false);
      const msg = isUpdate
        ? t("notes.toastMessage.noteUpdated", defaultContent.notes.toastMessage.noteUpdated as string)
        : t("notes.toastMessage.noteAdded", defaultContent.notes.toastMessage.noteAdded as string);
      setToastMessage(null);
      setTimeout(() => setToastMessage(msg), 0);
    } catch (error) {
      notesOnError(error as Error);
    }
  };

  const handleDelete = async () => {
    if (noteSaveDisabled) return;
    if (!note) {
      setShowDeleteConfirm(false);
      return;
    }

    setIsDeleted(true);
    setSavedContent(null);
    setSavedTimestamp(null);
    setShowDeleteConfirm(false);
    try {
      await deleteNote({ noteId: note.noteId }).unwrap();
      const deleteMsg = t("notes.toastMessage.noteDeleted", defaultContent.notes.toastMessage.noteDeleted as string);
      setToastMessage(null);
      setTimeout(() => setToastMessage(deleteMsg), 0);
    } catch (error) {
      setIsDeleted(false);
      notesOnError(error as Error);
    }
  };

  useEffect(() => {
    if (noteSaveDisabled) {
      setShowDeleteConfirm(false);
      setEditMode(false);
    }
  }, [noteSaveDisabled]);

  useEffect(() => {
    if (toastMessage === null) return;
    const timer = setTimeout(() => setToastMessage(null), TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  if (isNotesLoading) {
    return (
      <div className="DownlineProfileNotes__loader">
        <LeafRectangleLoader isLoading width="55%" height="1rem" margin="0 0 1rem 0" />
        <LeafRectangleLoader isLoading width="60%" height="1em" margin="0" />
        <LeafRectangleLoader isLoading width="70%" height="1rem" margin=".5rem 0" />
        <LeafRectangleLoader isLoading width="50%" height="1rem" margin="0" />
        <div className="DownlineProfileNotes__loader--cta">
          <LeafRectangleLoader isLoading width="5rem" height="1rem" margin="0" />
          <LeafRectangleLoader isLoading width="5rem" height="1rem" margin="0" />
        </div>
      </div>
    );
  }

  return (
    <div className="DownlineProfileNotes">
      <div className="DownlineProfileNotes__infoText">{t("notes.intoText", defaultContent.notes.intoText)}</div>
      {toastMessage && (
        <LeafToastMessage
          showToast
          theme="success"
          autoClose={TOAST_DURATION_MS}
          position="top-center"
          closeOnClick={false}
          pauseOnHover={false}
          draggable={false}
          className="notes-toast"
          htmlContent={
            <div className="notes-toast">
              <div className="notes-toast__icon">
                <IconCheckCircle size="sm" color="green" />
              </div>
              <div className="notes-toast__title">{toastMessage}</div>
            </div>
          }
        />
      )}

      {!hasNotes && !editMode && (
        <div className="DownlineProfileNotes__no-notes">
          <IconNoteCreate />
          <div className="DownlineProfileNotes__no-notes-subtext">
            <span>{t("notes.noNotes", defaultContent.notes.noNotes as string)}</span>{" "}
            <button
              onClick={() => {
                if (noteSaveDisabled) return;
                setEditMode(true);
                trackAddNewNote();
              }}
              disabled={noteSaveDisabled}
            >
              {t("notes.addNote", defaultContent.notes.addNote as string)}
            </button>
          </div>
        </div>
      )}

      {!editMode && hasNotes && noteContent && (
        <NotesViewer
          content={noteContent}
          lastEdited={lastEdited}
          actionsDisabled={noteSaveDisabled}
          onEdit={() => {
            if (noteSaveDisabled) return;
            setEditMode(true);
            trackEditNote();
          }}
          onDelete={() => {
            if (noteSaveDisabled) return;
            setShowDeleteConfirm(true);
            trackDeleteNoteModalOpen();
          }}
        />
      )}

      {editMode && (
        <NotesEditor
          initialContent={noteContent}
          onSave={(state) => {
            handleSave(state);
            trackSaveNote();
          }}
          onCancel={() => {
            setEditMode(false);
            trackCancelNoteChanges();
          }}
          isSaving={isSaving}
          saveDisabled={noteSaveDisabled}
        />
      )}

      <LeafModal
        open={showDeleteConfirm}
        id="notes-delete-modal"
        title={t("notes.deleteModal.title", defaultContent.notes.deleteModal.title as string)}
        description={t("notes.deleteModal.description", defaultContent.notes.deleteModal.description as string)}
        onCloseCallbackFn={() => {
          setShowDeleteConfirm(false);
          trackCloseNotesModal();
        }}
        backdropStyle="frosted"
      >
        <div className="DownlineProfileNotes__delete-actions">
          <LeafButton
            clickAction={() => {
              setShowDeleteConfirm(false);
              trackCloseNotesModal();
            }}
            id="notes-delete-cancel"
            title={t("notes.deleteModal.cancelButton", defaultContent.notes.deleteModal.cancelButton as string)}
          >
            {t("notes.deleteModal.cancelButton", defaultContent.notes.deleteModal.cancelButton as string)}
          </LeafButton>
          <LeafButton
            clickAction={() => {
              if (noteSaveDisabled) return;
              trackDeleteNote();
              handleDelete();
            }}
            id="notes-delete-confirm"
            title={t("notes.deleteModal.confirmButton", defaultContent.notes.deleteModal.confirmButton as string)}
            variant={Variant.SOLID}
            isDisabled={isDeleting || noteSaveDisabled}
          >
            {t("notes.deleteModal.confirmButton", defaultContent.notes.deleteModal.confirmButton as string)}
          </LeafButton>
        </div>
      </LeafModal>
    </div>
  );
};

export default Notes;
